import { useToast } from 'vue-toastification'
import type { AxiosError } from 'axios'
import { useErrorStore } from '~/stores/ErrorStore'
import { handleSessionExpired, isLoggingOut } from '~/services/auth/useLogoutService'
import type { ErrorMessage, ValidationErrors } from '~/ts/globals'

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Opt a request out of the global error toast. For best-effort calls whose
     * failures are not actionable (e.g. realtime lock release/heartbeat on an
     * agenda that may already have been replaced/deleted).
     */
    skipGlobalErrorToast?: boolean
  }
}

type ToastID = string | number

/**
 * Deduplication strategy: vue-toastification does not dedupe by id natively, so
 * we track ids of toasts that are currently visible and skip creating a toast
 * whose id is already active. Each toast's onClose removes its id from the set.
 */
const activeToastIds = new Set<ToastID>()

const showDedupedToast = (variant: 'error' | 'warning', message: string, id: ToastID): void => {
  if (activeToastIds.has(id)) return
  activeToastIds.add(id)

  const toast = useToast()
  const options = {
    id,
    onClose: () => {
      activeToastIds.delete(id)
    },
  }

  if (variant === 'warning') {
    toast.warning(message, options)
  } else {
    toast.error(message, options)
  }
}

const isCanceledError = (error: AxiosError): boolean =>
  error.code === 'ERR_CANCELED' || error.name === 'CanceledError'

/**
 * The cockpit shell keeps rendering behind the privacy/terms wizard, so its
 * data calls 403 until the user accepts. The wizard is already on screen and
 * is the correct affordance — a toast behind it is noise.
 */
const isTermsAcceptanceRequired = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  (error.response?.data as { code?: string } | undefined)?.code === 'terms_acceptance_required'

/**
 * Central error handler invoked by the axios response interceptor.
 * Classifies the error and routes to the right side-effect (toast, form errors,
 * or logout). Never rejects — the caller re-rejects so consumers still see
 * the error.
 */
export const handleApiError = (error: AxiosError): void => {
  if (isCanceledError(error)) return

  if (isLoggingOut()) return

  if (error.code === 'ERR_NETWORK') {
    // A page that opted out shows the failure itself (e.g. the sign-in pages).
    if (error.config?.skipGlobalErrorToast) return
    showDedupedToast(
      'error',
      'Server is not responding or please check your internet connection.',
      'network-error',
    )
    return
  }

  const status = error.response?.status

  if (status === 401) {
    handleSessionExpired()
    return
  }

  if (isTermsAcceptanceRequired(error)) return

  // Best-effort requests opt out of the toast (still after 401 so session
  // expiry is always handled). Their failures aren't actionable to the user.
  if (error.config?.skipGlobalErrorToast) return

  const errorStore = useErrorStore()

  if (status === 422) {
    const validationErrors = error.response?.data as ValidationErrors | undefined
    if (validationErrors?.errors) {
      errorStore.addFormErrors(validationErrors.errors)
      return
    }
    // A 422 without field errors is a domain rejection (e.g. a booking
    // conflict / double-booking) — fall through to the message toast below.
  }

  const apiError = error.response?.data as ErrorMessage | undefined
  if (!apiError?.message) return

  errorStore.addApiErrors(apiError)

  let id: ToastID
  if (status === 403) {
    id = 'forbidden'
  } else if (status && status >= 500) {
    id = 'server-error'
  } else {
    id = `api-error:${apiError.message}`
  }

  showDedupedToast('error', apiError.message, id)
}

/**
 * Test-only helper: clear the active toast-id set between test cases so dedup
 * state does not leak between tests.
 */
export const __resetApiErrorHandlerForTests = (): void => {
  activeToastIds.clear()
}
